import React from "react";
import { Card } from "../../features/ui/card";
import { Skeleton } from "../../features/ui/skeleton";

interface DataLoaderProps {
  variant?: "revenue-chart" | "overviewalerts" | "kpi" | "top-products" | "orders-table";
  className?: string;
  itemCount?: number; // Optional: customizable item count
}

const DataLoader: React.FC<DataLoaderProps> = ({ variant = "list", className = "", itemCount = 4 }) => {
  switch (variant) {
    case "revenue-chart":
      return (
      <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft">
      <div className={className} aria-busy="true" role="status">
        <div className="flex justify-end space-x-2 m-3  ">
        <Skeleton className="h-6 w-10 bg-secondary" />
        <Skeleton className="h-6 w-10 bg-secondary" />
        </div>
        <div className="flex flex-row m-3 space-x-2">
        {/* Larger chart area */}
        <Skeleton className="h-60 w-6 rounded-lg bg-secondary" />
        <Skeleton className="h-60 w-full rounded-lg bg-secondary" />         
        </div>
        <Skeleton className="h-6 w-100 rounded-md m-3 bg-secondary" />  
    </div>
    </Card>
      );

    case "overviewalerts":
      return (
        <div className="space-y-6">
          <div className="space-y-3" style={{ maxHeight: "200px", overflowY: "auto" }}>
            {Array(itemCount ?? 4).fill(0).map((_, i) => (
              <div
                key={i}
                className="flex items-start justify-between rounded-lg border p-3 animate-pulse"
              >
                <div>
                  <Skeleton className="h-4 w-32 mb-1" />
                  <Skeleton className="h-3 w-24 mb-1" />
                  <Skeleton className="h-4 w-40" />
                </div>
                <div className="flex flex-col items-end">
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "kpi":
      return (
        <div className={`w-full ${className}`} aria-busy="true" role="status">
          <Skeleton className="h-12 w-100 rounded-md" />
          <Skeleton className="h-5 w-3/4 mt-2 rounded-md  " />
        </div>
      );

    case "top-products":
      return (
        <div className={`space-y-2 ${className}`} aria-busy="true" role="status">
          <div className="max-h-96 overflow-y-auto space-y-2">
            {[...Array(itemCount)].map((_, i) => (
              <div
                key={i}
                className="rounded-lg flex items-center justify-between gap-4 p-2 hover:bg-muted"
              >
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-md" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-5 w-16 rounded-md" />
              </div>
            ))}
          </div>
        </div>
      );
      case "orders-table":
  return (
    <Card className="bg-white/70 dark:bg-black/70 rounded-xl shadow-xl-soft" aria-busy="true" role="status">
      <div className="flex flex-col space-y-2 p-4">
        {/* Header skeleton */}
        <div className="grid grid-cols-[40px_1fr_1fr_1fr_1fr_1fr_1fr_40px] gap-4 mb-2">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-6 w-full rounded-md bg-secondary" />
          ))}
        </div>

        {/* Rows skeleton */}
        {[...Array(itemCount)].map((_, i) => (
          <div
            key={i}
            className="grid grid-cols-[40px_1fr_1fr_1fr_1fr_1fr_1fr_40px] gap-4 items-center animate-pulse "
          >
            {/* Checkbox */}
            <Skeleton className="h-4 w-4 rounded bg-secondary" />
            {/* Order number and customer */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full bg-secondary" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-20 rounded bg-secondary" />
                <Skeleton className="h-3 w-16 rounded bg-secondary" />
              </div>
            </div>
            {/* Date */}
            <Skeleton className="h-4 w-16 rounded bg-secondary" />
            {/* Status */}
            <Skeleton className="h-5 w-20 rounded bg-secondary" />
            {/* Items */}
            <Skeleton className="h-4 w-12 rounded bg-secondary" />
            {/* Total */}
            <Skeleton className="h-5 w-16 rounded bg-secondary" />
            {/* Payment */}
            <Skeleton className="h-4 w-20 rounded bg-secondary" />
            {/* Actions button */}
            <Skeleton className="h-8 w-8 rounded bg-secondary" />
          </div>
        ))}
      </div>
    </Card>
  );

  }
};

export default DataLoader;
